/**
 * @file fetchHistoriesService.js
 * @description カスタマイズ設定値履歴の取得を行う共通サービス
 */

import apiService from "../../share/util/api/service";
import { executeSyncRequest } from "../../share/util/api/syncRequest";

/**
 * カスタマイズ設定値履歴一覧取得をマスター・ミラー両方から行い、統合した結果を返す
 * @param {object} self 呼び出し元の this を指定する
 * @param {string} netAppId クラウドコネクターID
 * @param {string} systemId システムID
 * @returns {{ customizeHistories: object[]; validateErrorResult: object; hasUnexpectedError: boolean; }} カスタマイズ設定値履歴一覧とエラー発生の有無を返す
 * - customizeHistories: カスタマイズ設定値履歴一覧 ※片方のリージョンのみ失敗した場合は、成功したリージョンのデータのみが入る。どちらも失敗した場合は空配列が入る。
 * - validateErrorResult: API のレスポンスチェックでエラーが発生した場合の結果（エラーが発生しなかった場合は null） ※エラーが発生した場合は、最初のリージョンで発生したエラーの結果が入る。
 * - hasUnexpectedError: API 呼び出しで予期せぬエラーが発生したかどうかのフラグ（true: 発生した、false: 発生しなかった） ※エラーが発生した場合は、最初のリージョンで発生したエラーの結果が入る。
 */
export const fetchMasterMirrorCustomizeHistories = async (
  self,
  netAppId,
  systemId,
) => {
  const regionInfoList = ["master", "mirror"];
  let validateErrorResult = null;
  let hasUnexpectedError = false;
  const customizeHistories = [];

  // Get APIを呼び出す（master/mirrorをそれぞれ取得）
  for (const regionInfo of regionInfoList) {
    const response = await executeSyncRequest(
      self,
      async () =>
        await apiService.getParamCall(
          apiService.CST,
          `/netApps/${netAppId}/systems/${systemId}/customize/histories`,
          { region_info: regionInfo },
        ),
      // エラー発生時はフラグを保持
      () => {
        hasUnexpectedError = true;
      },
      // レスポンスチェックはデフォルト処理
      undefined,
      // handler.validate でエラーが発生した場合は、最初の一度だけ保持
      (result) => {
        if (!validateErrorResult) {
          validateErrorResult = result;
        }
      },
    );

    const merged = (response?.data || []).map((item) => {
      return {
        ...item,
        region_info: regionInfo, // どちらのリージョンから取得したデータか識別するための情報を追加
      };
    });
    customizeHistories.push(...merged);
  }

  return { customizeHistories, validateErrorResult, hasUnexpectedError };
};
